import React from 'react';
import { useNavigate } from 'react-router-dom';

// Same host rule as App.js's isAdminRootHost(): on crm.* / localhost the
// admin CRM is at the root, everywhere else it lives under /admin.
const h = typeof window !== 'undefined' ? window.location.hostname : '';
export const ADMIN_BASE = (h === 'localhost' || h === '127.0.0.1' || h.startsWith('crm.')) ? '' : '/admin';
export const MBASE = `${ADMIN_BASE}/m`;

// Where to land after signing in: the phone screens when the CRM is being
// used as the installed home-screen app (or was last opened at /m in this
// tab) by an admin/manager; the normal desktop dashboard otherwise.
export function wantsMobile() {
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true
      || sessionStorage.getItem('crm_mobile') === '1';
  } catch { return false; }
}
export function homeFor(user) {
  return user && ['admin', 'manager'].includes(user.role) && wantsMobile() ? MBASE : `${ADMIN_BASE}/`;
}

export function money(value, currency = 'INR') {
  const v = Number(value) || 0;
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: v % 1 ? 2 : 0 }).format(v);
  } catch {
    return `${currency || ''} ${v.toLocaleString('en-IN')}`;
  }
}

export function when(d) {
  if (!d) return '';
  // SQLite CURRENT_TIMESTAMP is UTC without a zone marker.
  const dt = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(d) || d.length <= 10 ? d : `${d.replace(' ', 'T')}Z`);
  const mins = Math.round((Date.now() - dt.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24 && dt.getDate() === new Date().getDate()) return dt.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
  return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', ...(dt.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) });
}

const STATUS_LABEL = {
  processing: 'Processing', packing: 'Packing', ready: 'Ready to ship', dispatched: 'Dispatched', in_transit: 'In transit',
  shipped: 'Shipped', delivered: 'Delivered', completed: 'Completed', cancelled: 'Cancelled',
};
export const ORDER_STATUSES = Object.keys(STATUS_LABEL);
export const statusLabel = s => STATUS_LABEL[s] || s || '';

export function OrderStatusPill({ o }) {
  if (o.approval_status === 'pending' && o.status !== 'cancelled') return <span className="m-pill amber">Needs approval</span>;
  if (o.approval_status === 'rejected') return <span className="m-pill red">Rejected</span>;
  const cls = { delivered: 'green', completed: 'green', cancelled: 'red', dispatched: 'blue', in_transit: 'blue', shipped: 'blue' }[o.status] || '';
  return <span className={`m-pill ${cls}`}>{statusLabel(o.status)}</span>;
}

export function PayPill({ s }) {
  if (!s) return null;
  const map = { paid: ['Paid', 'green'], full: ['Paid', 'green'], partial: ['Part paid', 'amber'], unpaid: ['Unpaid', 'red'], pending: ['Payment pending', 'amber'] };
  const [label, cls] = map[s] || [s, ''];
  return <span className={`m-pill ${cls}`}>{label}</span>;
}

// Call / WhatsApp / Email buttons. WhatsApp needs digits only; a 10-digit
// Indian mobile gets the 91 country code.
export function ContactButtons({ phone, whatsapp, email }) {
  const digits = String(whatsapp || phone || '').replace(/\D/g, '');
  const wa = digits.length === 10 ? `91${digits}` : digits;
  if (!phone && !whatsapp && !email) return null;
  return (
    <div className="m-contact">
      {(phone || whatsapp) && <a href={`tel:${String(phone || whatsapp).replace(/[^\d+]/g, '')}`}><i className="ti ti-phone" />Call</a>}
      {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer"><i className="ti ti-brand-whatsapp" />WhatsApp</a>}
      {email && <a href={`mailto:${email}`}><i className="ti ti-mail" />Email</a>}
    </div>
  );
}

// Page header with an optional back button.
export function MTop({ title, back, right }) {
  const navigate = useNavigate();
  return (
    <header className="m-top">
      {back && <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(MBASE))} aria-label="Back"><i className="ti ti-arrow-left" /></button>}
      <h1>{title}</h1>
      {right}
    </header>
  );
}

export const errMsg =(e, fallback = 'Something went wrong') => e?.response?.data?.error || e?.response?.data?.message || e?.userMessage || fallback;
